import React, { useMemo } from 'react';
import { cn } from '../../poker-ui/utils/cn';
import {
  belotComboLabelShort,
  comboLabelPrefixFromComboKey,
  isCarreComboKey,
} from './belotComboLabelUi';

const { detectCombinations, declarationComboKey } = require('../../shared/game/belot/armenian/declarations');

/**
 * @param {string} comboKey
 */
function shortFromComboKey(comboKey) {
  return belotComboLabelShort(comboLabelPrefixFromComboKey(comboKey));
}

/**
 * Компактные индикаторы объявлений у места (торги — отдельно {@link BelotAuctionSeatActionBubble}).
 *
 * @param {{
 *   phase?: string,
 *   declarationMode?: string,
 *   declaredComboKeys?: string[]|null,
 *   casualDeclareHand?: number[]|null,
 *   declarationBattleOutcome?: 'team_a'|'team_b'|'tie'|null,
 *   declarationBattleApply?: boolean,
 *   seatTeamIndex?: number,
 * }} props
 */
export function BelotSeatSignalStrip({
  phase = '',
  declarationMode = 'casual',
  declaredComboKeys = null,
  casualDeclareHand = null,
  declarationBattleOutcome = null,
  declarationBattleApply = false,
  seatTeamIndex,
}) {
  const declMode = declarationMode === 'hardcore' ? 'hardcore' : 'casual';

  const declarePills = useMemo(() => {
    const keys = Array.isArray(declaredComboKeys) ? declaredComboKeys.filter(Boolean) : [];
    if (keys.length > 0) {
      const seen = new Set();
      /** @type {{ short: string, carre: boolean }[]} */
      const rows = [];
      for (const k of keys) {
        const sh = shortFromComboKey(String(k));
        if (!sh || seen.has(sh)) continue;
        seen.add(sh);
        rows.push({ short: sh, carre: isCarreComboKey(String(k)) });
      }
      return rows.slice(0, 5);
    }
    if (phase === 'declare' && declMode === 'casual' && Array.isArray(casualDeclareHand) && casualDeclareHand.length === 8) {
      try {
        const combos = detectCombinations(casualDeclareHand);
        combos.sort((a, b) => (b.points || 0) - (a.points || 0));
        const seen = new Set();
        /** @type {{ short: string, carre: boolean }[]} */
        const rows = [];
        for (const c of combos) {
          const k = declarationComboKey(c);
          if (seen.has(k)) continue;
          seen.add(k);
          const sh = belotComboLabelShort(c.label);
          if (sh) rows.push({ short: sh, carre: isCarreComboKey(k) });
        }
        return rows.slice(0, 5);
      } catch {
        return [];
      }
    }
    return [];
  }, [declMode, declaredComboKeys, casualDeclareHand, phase]);

  const hideAsLostTeam =
    declarationBattleApply === true &&
    (declarationBattleOutcome === 'team_a' || declarationBattleOutcome === 'team_b') &&
    typeof seatTeamIndex === 'number' &&
    Number.isFinite(seatTeamIndex) &&
    ((declarationBattleOutcome === 'team_a' && seatTeamIndex === 1) ||
      (declarationBattleOutcome === 'team_b' && seatTeamIndex === 0));

  if (declarePills.length === 0) return null;

  return (
    <div
      className={cn(
        'pointer-events-none mt-0.5 flex max-w-[min(104px,22vw)] flex-wrap justify-center gap-0.5 sm:max-w-[120px]',
        'transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none motion-reduce:duration-0',
        hideAsLostTeam
          ? 'is-loser opacity-0 scale-[0.95]'
          : 'opacity-100 scale-100',
      )}
      aria-hidden
    >
      {declarePills.map((row, i) => {
        const lab = row.short;
        const carre = row.carre;
        return (
          <span
            key={`d-${i}-${lab}`}
            className={cn(
              'inline-flex min-h-[14px] min-w-[14px] shrink-0 items-center justify-center rounded-md border px-1 py-[1px]',
              'font-mono text-[8px] font-bold leading-none',
              carre
                ? 'border-amber-300/55 bg-gradient-to-b from-amber-950/85 to-black/70 text-amber-100 shadow-[0_0_12px_rgba(251,191,36,0.45)]'
                : 'border-cyan-400/35 bg-cyan-950/45 text-cyan-100 shadow-[0_0_8px_rgba(34,211,238,0.18)]',
            )}
            title="Объявление"
          >
            {lab}
          </span>
        );
      })}
    </div>
  );
}
